/* CEO OFFICE — Young, 2026-09-14; Max promised him the Tickets screen and the event log in week
   one (2026-09-15), which is this.

   THREE THINGS YOUNG SAID MATTER, and each shows up here as something the screen does NOT do:

   1. The counters are calculated. Not one of the six cards reads a stored figure — they all come
      back computed from `00_PORTAL_ceo_ticket_log`, so there is no number on this page anybody
      can type.
   2. The desk is access. There is no "all desks" option, because the server would refuse it
      anyway; the switcher only ever offers the desks the caller holds, and a person on one desk
      gets no switcher at all rather than a disabled one that names the other.
   3. Entity and internal contact are nullable, and empty is the NORMAL state — "many items start
      with no home". An unset entity prints as a marked word rather than a blank, because blank
      reads as "nobody filled this in yet" and the card above counts it on purpose. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import {
  Plus, RefreshCw, MessageSquare, X, Mail, CheckCircle2, RotateCcw, StickyNote, Paperclip,
} from 'lucide-react';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import ListPicker from './ListPicker';
import useEscapeClose from './useEscapeClose';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import TabIcon from './TabIcon';
import { BACKEND_URL } from './backendConfig';
import './CeoOffice.css';

const API = `${BACKEND_URL}/api/ceo/office`;
const CEO_FILE_MAX = 10;            // matches the server's per-ticket cap
/* KB under a megabyte, MB above it, one decimal either way — "0.1 MB" reads as nothing and
   "1433 KB" reads as a number rather than a size. */
const fmtBytes = (n) => (!n ? '' : n < 1048576 ? `${Math.max(1, Math.round(n / 1024))} KB`
  : `${(n / 1048576).toFixed(1)} MB`);

const fmtDate = (d) => (d ? String(d).slice(0, 10) : '');
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/* IN THE READER'S OWN CLOCK, with the month as a word — 2026-10-20, for Young's correspondence
   history. It printed the stored UTC, which is a time nobody on these desks lives in: a mail Young
   sent at 21:56 in Singapore read 13:56, beside a reply that came "before" it by his watch. */
const fmtWhen = (d) => {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  const p2 = (n) => String(n).padStart(2, '0');
  return `${p2(x.getDate())}/${MON[x.getMonth()]}/${x.getFullYear()} ${p2(x.getHours())}:${p2(x.getMinutes())}`;
};

/* WHERE A REPLY STOPS BEING NEW. Every client appends the conversation under what was just
   written, so the fourth mail of a thread carries the other three again; printed whole, the
   history says everything four times. The first quote marker found is where the new part ends —
   Gmail's "On … wrote:" (it wraps onto a second line when the name is long), Outlook's header
   block, a ">" line, Korean Gmail's "작성:". Nothing is dropped: the rest is one press away. */
const QUOTE_MARKS = [
  /^On [^\n]+(\n[^\n]+)?\swrote:\s*$/m,
  /^-{2,}\s*Original Message\s*-{2,}/mi,
  /^From:\s.+\n(Sent|Date):\s/m,
  /^[^\n]*님이 작성:\s*$/m,
  /^>/m,
];
const splitQuoted = (body) => {
  const text = String(body || '');
  let cut = -1;
  for (const re of QUOTE_MARKS) {
    const m = text.match(re);
    if (m && m.index > 0 && (cut < 0 || m.index < cut)) cut = m.index;
  }
  return cut < 0 ? [text.trim(), ''] : [text.slice(0, cut).trim(), text.slice(cut).trim()];
};

/* THE FIELD A CHANGE LINE NAMES, in the words the form above it uses. */
const FIELD_LABEL = {
  title: 'ceo.fTitle', type: 'ceo.colType', sender: 'ceo.fromWho', entity: 'ceo.colEntity',
  internalContact: 'ceo.colContact', owner: 'ceo.colOwner', status: 'ceo.colStatus',
  dueDate: 'ceo.colDue', desk: 'ceo.colDesk',
};

/* ONE TICKET'S STORY IN THE ORDER IT HAPPENED — Young, 2026-10-20: "the actual correspondence in
   chronological order … who sent what, to whom, and when … internal notes and status changes also
   visible, clearly distinguished from emails." The mails come from their own table; the log rows a
   mail produced (`mailPk`) are what the counters read and are not printed a second time. */
const HIST_GROUP = { mail: 'mail', note: 'note', contact: 'note', change: 'change' };
const historyItems = (log, mails) => {
  const kindOf = (l) => {
    if (l.event === 'note') return 'note';
    if (l.event === 'contacted' || l.event === 'reply') return 'contact';
    return 'change';
  };
  return [
    ...(mails || []).map((m) => ({ kind: 'mail', at: m.at, key: `m${m.id}`, mail: m })),
    ...(log || []).filter((l) => !l.mailPk).map((l) => ({ kind: kindOf(l), at: l.at, key: `l${l.id}`, log: l })),
  ].sort((a, b) => (new Date(a.at) - new Date(b.at)) || (a.key < b.key ? -1 : 1));
};

/* THE SIX COUNTERS, in the mockup's order. Each carries the RULE it counts by, because a figure a
   desk is meant to act on has to say what it means — "untouched 3+ days" is only useful once you
   know it counts from the last contact and not from the raised date. */
const CARDS = [
  { key: 'inProgress', tk: 'cardOpen', tone: '' },
  { key: 'pastDeadline', tk: 'cardOverdue', tone: 'bad' },
  { key: 'untouched', tk: 'cardUntouched', tone: 'warn' },
  { key: 'waitingCeo', tk: 'cardWaiting', tone: 'ceo' },
  { key: 'entityUnset', tk: 'cardNoEntity', tone: 'muted' },
  { key: 'closedThisWeek', tk: 'cardClosed', tone: 'good' },
];

/* The stage colours follow what a stage MEANS: blue while it is moving, amber once it is with the
   CEO, grey once it is closed — the same four tones the rest of this section uses. */
const LEAD_TONE = { Contacted: '', Replied: 'ceo', 'Checking criteria': 'warn', 'With CEO': 'warn', Closed: 'muted' };

/* WHAT EACH COLUMN IS, for sorting and for its funnel. A date sorts as a date and a D-day as a
   number — as text, 10 comes before 9, which only shows once the column can be sorted. */
const CEO_GETTERS = {
  ticketId: (r) => r.ticketId,
  title: (r) => r.title,
  desk: (r) => r.desk,
  type: (r) => r.type || '—',
  entity: (r) => r.entity || '—',
  internalContact: (r) => r.internalContact || '—',
  owner: (r) => r.owner || '—',
  dueDate: (r) => (r.dueDate ? String(r.dueDate).slice(0, 10) : '—'),
  status: (r) => r.status,
  daysAtStatus: (r) => (r.daysAtStatus === null || r.daysAtStatus === undefined ? '' : String(r.daysAtStatus)),
  lastContactAt: (r) => (r.lastContactAt ? String(r.lastContactAt).slice(0, 10) : '—'),
  dDay: (r) => (r.dDay === null || r.dDay === undefined ? '—' : String(r.dDay)),
};
const CEO_SORTERS = {
  ...Object.fromEntries(Object.keys(CEO_GETTERS).map((k) => [k, (r) => CEO_GETTERS[k](r)])),
  dueDate: (r) => (r.dueDate ? new Date(r.dueDate).getTime() : null),
  lastContactAt: (r) => (r.lastContactAt ? new Date(r.lastContactAt).getTime() : null),
  dDay: (r) => (r.dDay === null || r.dDay === undefined ? null : r.dDay),
  // Sorted as a NUMBER, or 10 days lands before 9 — the same trap the D-Day export column hit.
  daysAtStatus: (r) => (r.daysAtStatus === null || r.daysAtStatus === undefined ? null : r.daysAtStatus),
};
const LEAD_GETTERS = {
  leadId: (r) => r.leadId,
  counterparty: (r) => r.counterparty,
  country: (r) => r.country || '—',
  sector: (r) => r.sector || '—',
  stage: (r) => r.stage,
  criteria: (r) => r.criteria || '—',
  lastContactAt: (r) => (r.lastContactAt ? String(r.lastContactAt).slice(0, 10) : '—'),
  nextContact: (r) => (r.nextContact ? String(r.nextContact).slice(0, 10) : '—'),
  ticketId: (r) => r.ticketId || '—',
};
const LEAD_SORTERS = {
  ...Object.fromEntries(Object.keys(LEAD_GETTERS).map((k) => [k, (r) => LEAD_GETTERS[k](r)])),
  lastContactAt: (r) => (r.lastContactAt ? new Date(r.lastContactAt).getTime() : null),
  nextContact: (r) => (r.nextContact ? new Date(r.nextContact).getTime() : null),
};

/* The column key is what the funnel is stored under; this is what a person calls it. One table, so
   the strip that names a filter and the header it came from cannot say two different things. */
const CEO_COL_LABEL = {
  ticketId: 'ceo.colTicket', title: 'ceo.colTitle', desk: 'ceo.colDesk', type: 'ceo.colType',
  entity: 'ceo.colEntity', internalContact: 'ceo.colContact', owner: 'ceo.colOwner',
  dueDate: 'ceo.colDue', status: 'ceo.colStatus', daysAtStatus: 'ceo.colDwell',
  lastContactAt: 'ceo.colLast', dDay: 'ceo.colDday',
};
const LEAD_COL_LABEL = {
  leadId: 'ceo.colLead', counterparty: 'ceo.colCounterparty', country: 'ceo.colCountry',
  sector: 'ceo.colSector', stage: 'ceo.colStage', criteria: 'ceo.colCriteria',
  lastContactAt: 'ceo.colLast', nextContact: 'ceo.colNext', ticketId: 'ceo.colTicket',
};

/* THE TICKET'S OWN VOCABULARY, and only its own — Max, 2026-09-22, reading the build against
   Young's mockup. `Checking criteria` was in this list AND is a LEAD stage, which is the fault
   this codebase keeps fixing: one word doing two jobs on one screen, so a reader cannot tell
   whether "Checking criteria" means a document arrived or a counterparty was measured against the
   CEO's terms. It belongs to the lead and is gone from here.
   `Awaiting files` came the other way — it is in Young's mockup and two tickets were carrying it,
   while the dropdown did not offer it, so opening one of those to change anything else silently
   had no way back to the status it was already at. */
const STATUSES = ['Open', 'In progress', 'Awaiting files', 'Waiting on CEO', 'Closed', 'Dropped'];

/* The channels offered first time out. Not a table and not a closed list — the picker adds to it,
   and it grows out of the log itself, because "we called them" is not a thing anybody should have
   to be granted permission to say. */
const CEO_CHANNELS = ['Email', 'Phone', 'WhatsApp', 'KakaoTalk', 'Zalo', 'Meeting', 'Message'];

/* THE GUIDE (Max 01/11: every module has one, ending on the manual). It walks the Tickets tab, so it first switches back
   to it. The desk pills are drawn only for a person who holds more than one desk (or the intake), and that step is
   skipped for everyone else. */
const TOUR = [
  { selector: '.ceo-tabs', title: 'tourCeo.tabsT', body: 'tourCeo.tabsB' },
  { selector: '.ceo-desks', title: 'tourCeo.desksT', body: 'tourCeo.desksB' },
  { selector: '.ceo-bar .sp-btn-primary', title: 'tourCeo.newT', body: 'tourCeo.newB' },
  { selector: '.ceo-cards', title: 'tourCeo.cardsT', body: 'tourCeo.cardsB' },
  { selector: '.ceo-filters', title: 'tourCeo.filtersT', body: 'tourCeo.filtersB' },
  { selector: '.ceo-wrap table', title: 'tourCeo.tableT', body: 'tourCeo.tableB' },
];

export default function CeoOffice() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [desk, setDesk] = useState('');
  const [placing, setPlacing] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [openTicket, setOpenTicket] = useState(null);   // { ticket, log }
  const [adding, setAdding] = useState(null);
  const [card, setCard] = useState('');
  const [search, setSearch] = useState('');
  /* THE DROPDOWNS ARE GONE and the columns filter themselves — Max, 2026-09-22, asking for Excel's
     sort and filter on these tables. Keeping both would be the Product Line mistake of 2026-09-07,
     where a type dropdown shipped and came straight back out: the column already named every row
     and its funnel already filtered. */
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [cols, setCols] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [lsort, setLsort] = useState({ key: '', dir: 'asc' });
  const [lcols, setLcols] = useState({});
  const [lsearch, setLsearch] = useState('');
  const [lopenFilter, setLopenFilter] = useState(null);
  /* THREE SCREENS, one section — Young's mockup, and Max on 2026-09-22: "cứ làm xong hết luôn
     nha". They share the desk bar, because the desk is the section's rule and not one screen's. */
  const [tab, setTab] = useState('tickets');
  const [leads, setLeads] = useState(null);
  const [daily, setDaily] = useState(null);
  const [addLead, setAddLead] = useState(null);
  const [openLead, setOpenLead] = useState(null);
  const [leadLog, setLeadLog] = useState(null);   // null = not asked yet, [] = asked and empty
  const [hit, setHit] = useState('');             // the row a mail link pointed at
  // { id, kind, channel, note } while the contact dialog is open
  const [logging, setLogging] = useState(null);
  // { key, list } — the OWNER is carried with the list, so a panel can never draw the other
  // one's files for the frame between opening and the fetch coming back.
  const [files, setFiles] = useState({ key: '', list: [] });
  const [upBusy, setUpBusy] = useState('');
  const [dropFile, setDropFile] = useState(null);   // the file the dialog is asking about
  // { id, mode: 'close' | 'reopen', note, error } while the close / reopen dialog is open
  const [closing, setClosing] = useState(null);
  const [hist, setHist] = useState('all');           // which part of the history is shown
  const [quoted, setQuoted] = useState(() => new Set());   // mails whose quoted text is open

  const load = useCallback(async (which) => {
    setBusy(true); setError('');
    try {
      const q = which ? `?desk=${encodeURIComponent(which)}` : '';
      const res = await fetch(`${API}/tickets${q}`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xCeo.loadFailed'));
      setData(d);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, []);

  /* PLACING AN ITEM ON A DESK. The row leaves the queue the moment it succeeds, so the list is
     reloaded rather than patched: what the person is looking at has genuinely changed population.
     A 409 is the ordinary case on a shared queue — somebody else placed it first — and it is
     reported as what it is rather than as a failure. */
  const placeOnDesk = async (row, which) => {
    if (!which) return;
    setPlacing(row.ticketId);
    try {
      const res = await fetch(`${API}/tickets/${row.pk}/desk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ desk: which }),
      });
      const b = await res.json();
      if (!res.ok) throw new Error(b.error || t('xCeo.placeFailed'));
      await load(desk === 'unassigned' ? '' : desk);
    } catch (e) { setError(e.message); }
    finally { setPlacing(''); }
  };

  const loadLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API}/leads`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xCeo.leadsLoadFailed'));
      setLeads(d);
    } catch (e) { setError(e.message); }
  }, []);

  const loadDaily = useCallback(async (which) => {
    try {
      const res = await fetch(`${API}/daily${which ? `?desk=${encodeURIComponent(which)}` : ''}`,
        { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xCeo.dailyFailed'));
      setDaily(d);
    } catch (e) { setError(e.message); }
  }, []);

  /* 'unassigned' is NOT a desk on the wire. The server turns the queue off the moment a desk is
     asked for — choosing a desk is choosing to look at that desk — so the pill loads everything
     and the cut happens below, in `tickets`. */
  useEffect(() => { load(desk === 'unassigned' ? '' : desk); }, [load, desk]);
  /* Leads are fetched ON ARRIVAL, not when the tab is opened: the tab carries its own count and a
     bar that only learns the number once you have been there has already failed at the one thing
     it is for. Daily is not — it has no count to print, and it is four queries over the log. */
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { if (tab === 'leads') loadLeads(); }, [tab, loadLeads]);
  useEffect(() => { if (tab === 'daily') loadDaily(desk); }, [tab, desk, loadDaily]);

  /* 'unassigned' is a pill, not a desk on the wire — the same rule the effect below follows. The
     savers used to pass it straight through, so a save made while looking at the queue reloaded
     the list as "the unassigned desk", which the server answers with nothing. */
  const reloadList = () => load(desk === 'unassigned' ? '' : desk);

  const openOne = async (id) => {
    setError('');
    try {
      const res = await fetch(`${API}/tickets/${encodeURIComponent(id)}`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.ticketReadFailed'));
      setOpenTicket(d);
    } catch (e) { setError(e.message); }
  };

  const save = async (id, patch) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/tickets/${encodeURIComponent(id)}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.saveFailed'));
      await reloadList();
      await openOne(id);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  /* CLOSE AND REOPEN — Young, 2026-10-20. Its own action rather than a status in the dropdown,
     because it carries a note and says who did it; the refusal (already closed, changed by somebody
     else a moment ago) is shown INSIDE the dialog, where the person pressing is looking. */
  const closeReopen = async () => {
    if (!closing) return;
    const { id, mode, note } = closing;
    setBusy(true);
    try {
      const res = await fetch(`${API}/tickets/${encodeURIComponent(id)}/${mode}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: (note || '').trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.saveFailed'));
      setClosing(null);
      await reloadList();
      await openOne(id);
    } catch (e) { setClosing((c) => (c ? { ...c, error: e.message } : c)); } finally { setBusy(false); }
  };

  const recordContact = async (id, kind, { channel, note } = {}) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/tickets/${encodeURIComponent(id)}/contact`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, channel, note }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.recordFailed'));
      await reloadList();
      await openOne(id);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  /* Saved ONE AT A TIME, on blur, because eight boxes behind a single Save button is eight
     chances to lose seven of them to a mis-click. The server refuses anything that is not an
     address rather than storing it — a typo here is a board matter mailed to nobody. */
  const saveEntity = async (code, cc, revert) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/entities`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entities: [{ code, cc }] }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.saveFailed'));
      await reloadList();
    } catch (e) {
      setError(e.message);
      /* A REFUSED ADDRESS COMES BACK OUT OF THE BOX. The server would not store it, and a box
         still holding the rejected text reads as though it had — on a column that decides who
         receives a board matter, "looks saved" is the wrong thing for it to look like. */
      if (revert) revert();
    } finally { setBusy(false); }
  };

  /* EXPORT TO EXCEL, through the endpoint the Project Tracker and Ticket Station already use — it
     takes columns, rows and widths and knows nothing about which screen sent them.

     It exports the table AS IT STANDS, and the filter line NAMES every narrowing including the
     CARD, which is not one of the dropdowns: a file quietly holding only the overdue rows would be
     the same trap as a screen filtered by something it does not mention. */
  const exportXlsx = async () => {
    const stamp = new Date().toISOString().replace('T', ' ').slice(0, 16);
    const named = (k, v) => (v ? `${t(`ceo.col${k}`)}: ${v}` : null);
    const cols = [
      ['ceo.colTicket', 14], ['ceo.colTitle', 46], ['ceo.fromWho', 28], ['ceo.colDesk', 12],
      ['ceo.colType', 14], ['ceo.colEntity', 10], ['ceo.colContact', 18], ['ceo.colOwner', 16],
      ['ceo.colDue', 12], ['ceo.colDday', 9], ['ceo.colStatus', 18],
      ['ceo.colDwell', 12], ['ceo.colLast', 14],
    ];
    try {
      setBusy(true); setError('');
      const res = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: t('xCeo.exportTitle'), generated: stamp,
          filters: [
            desk ? `${t('ceo.desk')}: ${t(`ceo.desk_${desk}`)}` : `${t('ceo.desk')}: ${t('ceo.allDesks')}`,
            card ? `${t('ceo.colStatus')}: ${t(`ceo.${CARDS.find((c) => c.key === card).tk}`)}` : null,
            // Whatever the column funnels are narrowing by, so the file says what the screen says.
            ...Object.entries(cols).map(([k, v]) => `${k}: ${v.join(', ')}`),
            search ? `${t('ceo.searchPh')} ${search}` : null,
          ].filter(Boolean),
          columns: cols.map(([k]) => t(k)),
          widths: cols.map(([, w]) => w),
          /* `{ values }`, NOT a bare array — the endpoint reads `r.values` and a bare array gives
             it `undefined` for every column, so the file came out with its headings, its filter
             line and one blank row per ticket. It looked like a working export from the outside,
             which is why it took opening the file to find. */
          rows: tickets.map((r) => ({ values: [
            r.ticketId, r.title, r.restricted ? t('ceo.restricted') : (r.sender || ''),
            t(`ceo.desk_${r.desk}`), r.type || '', r.entity || '', r.internalContact || '',
            r.owner || '', fmtDate(r.dueDate),
            // A blank in a numeric column is null, not '' — one text cell makes Excel sort the
            // whole column as text, and then 10 comes before 9. True of both day columns.
            r.dDay === null || r.dDay === undefined ? null : r.dDay,
            r.status,
            r.daysAtStatus === null || r.daysAtStatus === undefined ? null : r.daysAtStatus,
            fmtDate(r.lastContactAt),
          ] })),
          sheetName: 'CEO Office',
          // 1-based, and they move with the columns: Due(9) D-day(10) Status(11) Days(12) Last(13).
          statusColumn: 11, freezeColumns: 2, autoFilter: true, numericColumns: [10, 12],
          filename: `HEVN_CEO_Office_${stamp.replace(/[: ]/g, '-')}.xlsx`,
        }),
      });
      if (!res.ok) throw new Error(t('xCeo.exportFailed', { status: res.status }));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `HEVN_CEO_Office_${stamp.replace(/[: ]/g, '-')}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  /* THE LEADS TABLE EXPORTS TOO — Max, 2026-09-22, checking the build against Young's mockup,
     which carries Export to Excel on both tables. It goes through the SAME endpoint and the same
     rule: the file is the table as it stands, and the filter line names every narrowing, so a
     sheet holding six of twenty-nine rows says on its own face why. */
  const exportLeadsXlsx = async () => {
    const stamp = new Date().toISOString().replace('T', ' ').slice(0, 16);
    const cols = [
      ['ceo.colLead', 14], ['ceo.colCounterparty', 30], ['ceo.colCountry', 16],
      ['ceo.colSector', 16], ['ceo.colStage', 18], ['ceo.colCriteria', 40],
      ['ceo.colLast', 14], ['ceo.colNext', 14], ['ceo.colTicket', 16],
    ];
    try {
      setBusy(true); setError('');
      const res = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: t('xCeo.exportTitleLeads'), generated: stamp,
          filters: ldNarrow,
          columns: cols.map(([k]) => t(k)),
          widths: cols.map(([, w]) => w),
          rows: leadRows.map((r) => ({ values: [
            r.leadId, r.counterparty || '', r.country || '', r.sector || '', r.stage || '',
            r.criteria || '', fmtDate(r.lastContactAt), fmtDate(r.nextContact), r.ticketId || '',
          ] })),
          sheetName: 'CEO Leads',
          statusColumn: 5, freezeColumns: 2, autoFilter: true,
          filename: `HEVN_CEO_Leads_${stamp.replace(/[: ]/g, '-')}.xlsx`,
        }),
      });
      if (!res.ok) throw new Error(t('xCeo.exportFailed', { status: res.status }));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `HEVN_CEO_Leads_${stamp.replace(/[: ]/g, '-')}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const saveLead = async (id, patch) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/leads/${encodeURIComponent(id)}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.saveFailed'));
      await loadLeads();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const leadContact = async (id, { channel, note } = {}) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/leads/${encodeURIComponent(id)}/contact`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, note }),
      });
      if (!res.ok) throw new Error(t('xCeo.recordFailed'));
      await loadLeads();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const createLead = async () => {
    if (!addLead?.counterparty?.trim()) { setError(t('ceo.needCounterparty')); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addLead),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.createFailed'));
      setAddLead(null); await loadLeads();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const create = async () => {
    if (!adding?.title?.trim()) { setError(t('ceo.needTitle')); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/tickets`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adding),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.createFailed'));
      setAdding(null);
      await reloadList();
      await openOne(d.ticketId);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  /* ONE ATTACHMENTS BLOCK for the ticket panel and the lead panel — Max, 2026-09-23: "bên lead
     cũng cần có attach file nhé". Two copies of this markup is two places for the Add file button
     to drift, or for one of them to keep an empty-state line the other has lost. */
  const filesBlock = () => {
    const list = files.list || [];
    return (
      <>
            <h4 className="ceo-fileshead">
          {t('ceo.files')} <em>{list.length ? `· ${list.length}` : ''}</em>
        </h4>
        <p className="ceo-filesub">{t('ceo.filesSub', { n: CEO_FILE_MAX })}</p>
        <div className={`ceo-files${list.length ? ' has' : ''}`}>
          {list.map((f) => (
            <div className="ceo-file" key={f.fileId}>
              {/* A DOWNLOAD, never a render — the server sends Content-Disposition, and this
                  opens in a new tab so a half-written ticket is not navigated away from. */}
              <a href={`${BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                className="ceo-filename" title={f.name}>{f.name}</a>
              <span className="ceo-filemeta">
                {fmtBytes(f.bytes)}{f.uploadedBy ? ` · ${f.uploadedBy}` : ''}
                {f.uploadedAt ? ` · ${fmtDate(f.uploadedAt)}` : ''}
              </span>
              <button type="button" className="ceo-filex" disabled={!!upBusy}
                onClick={() => setDropFile(f)} aria-label={t('ceo.removeFile')}
                title={t('ceo.removeFile')}>&times;</button>
            </div>
          ))}
          {/* NO EMPTY-STATE LINE — Max, 2026-09-23: "bỏ chữ Nothing attached yet. đi ko đẹp".
              The Add file button under it already says what the empty list is and what to do
              about it; a sentence saying "there is nothing here" above a button that adds
              something is the screen narrating itself. */}
          {!!upBusy && <p className="ceo-blkempty">{t('ceo.uploading')} {upBusy}</p>}
        </div>
        {list.length < CEO_FILE_MAX && (
          /* A LABEL, not a button that clicks a hidden input: a label IS the control for the
             input it wraps, so it is reachable by keyboard without a line of script. And it
             sits UNDER the list rather than inside it — it is not a file. */
          <div className="ceo-fileadd">
            <label className="sp-btn ceo-addfile">
              <Plus size={13} /> {t('ceo.addFile')}
              <input
                type="file"
                onChange={(e) => { addFile(e.target.files?.[0]); e.target.value = ''; }}
                disabled={!!upBusy}
              />
            </label>
          </div>
        )}

      </>
    );
  };

  /* ONE saver for both, because a contact on a lead and a contact on a ticket are the same act
     recorded in two tables — two dialogs would be two places for the note to be forgotten. */
  const saveContact = async () => {
    if (!logging) return;
    const { channel, note } = logging;
    if (logging.kind === 'note' && !(note || '').trim()) return;
    const payload = { channel: logging.kind === 'note' ? '' : (channel || ''), note: (note || '').trim() };
    if (logging.lead) await leadContact(logging.lead, payload);
    else await recordContact(logging.id, logging.kind, payload);
    setLogging(null);
  };

  /* The channels already used, so the list grows itself. It reads the LOG rather than a table: a
     channel is not a thing anybody should have to administer. */
  const channelOptions = useMemo(() => {
    const seen = new Set(CEO_CHANNELS);
    for (const l of (openTicket?.log || [])) if (l.event === 'contacted' || l.event === 'reply') {
      if (l.newValue) seen.add(l.newValue);
    }
    for (const l of (leadLog || [])) if (l.newValue && l.event === 'contacted') seen.add(l.newValue);
    return [...seen];
  }, [openTicket, leadLog]);

  /* ONE staleness rule, read by the UNTOUCHED card's filter and by the Last contact column, so a
     red date and a counted row are never two different opinions. */
  const staleContact = (r) => r.status !== 'Closed' && r.status !== 'Dropped'
    && Math.floor((Date.now() - new Date(r.lastContactAt || r.createdAt).getTime()) / 86400000) >= 3;

  const tickets = useMemo(() => {
    let all = data?.tickets || [];
    if (desk === 'unassigned') all = all.filter((r) => !r.desk);
    const q = search.trim().toLowerCase();
    if (q) {
      // Every word, in any order, over the title and whoever it came from.
      const words = q.split(/\s+/).filter(Boolean);
      all = all.filter((r) => {
        const hay = [r.title, r.sender, r.ticketId].filter(Boolean).join(' ').toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }
    if (card) {
    /* A CARD NARROWS THE TABLE and says so on the strip below it. The rules are the SAME ones the
       server counted with — written once here rather than guessed, so a card can never light up a
       number the table underneath cannot produce. */
    const days = (x) => (x ? Math.floor((Date.now() - new Date(x).getTime()) / 86400000) : null);
    const open = (r) => r.status !== 'Closed' && r.status !== 'Dropped';
      if (card === 'inProgress') all = all.filter(open);
      if (card === 'pastDeadline') all = all.filter((r) => open(r) && (r.dDay ?? -1) > 0);
      if (card === 'untouched') all = all.filter((r) => open(r) && (days(r.lastContactAt || r.createdAt) ?? 0) >= 3);
      if (card === 'waitingCeo') all = all.filter((r) => open(r) && r.status === 'Waiting on CEO');
      if (card === 'entityUnset') all = all.filter((r) => open(r) && !r.entity);
      if (card === 'closedThisWeek') {
        all = all.filter((r) => r.closedAt && new Date(r.closedAt).getTime() >= Date.now() - 7 * 86400000);
      }
    }
    /* The COLUMN filters come last and the sort after them, so a funnel's list only ever offers
       values the rows in front of it actually hold. */
    const afterCols = Object.keys(cols).length
      ? all.filter((r) => rowPassesColumnFilters(r, cols, CEO_GETTERS)) : all;
    return sort.key ? sortRows(afterCols, sort, CEO_SORTERS) : afterCols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, card, search, cols, sort, desk]);

  /* THE LEAD'S HISTORY, fetched when the panel opens rather than sent with every row: 29 leads
     would carry 29 logs to draw one. It is CLEARED first, or the panel shows the previous lead's
     history for as long as the request takes — which is the worst possible moment to be looking at
     somebody else's dates. */
  /* ESCAPE CLOSES IT, and the page behind stops scrolling while it is up. Both are what makes a
     popup a popup rather than a card drawn over the middle of the screen: without the first there
     is one way out and it is a 14px ×, and without the second a scroll gesture moves the table
     nobody can see. */
  useEscapeClose(() => { setOpenTicket(null); setOpenLead(null); }, !!(openTicket || openLead));
  // Registered after the panel's, so Escape closes the dialog on top and leaves the ticket open.
  useEscapeClose(() => setLogging(null), !!logging);
  useEscapeClose(() => setClosing(null), !!closing);

  const histAll = useMemo(() => historyItems(openTicket?.log, openTicket?.mails),
    [openTicket]);
  const histCounts = useMemo(() => {
    const n = { all: histAll.length, mail: 0, note: 0, change: 0 };
    histAll.forEach((x) => { n[HIST_GROUP[x.kind]] += 1; });
    return n;
  }, [histAll]);
  const histShown = hist === 'all' ? histAll : histAll.filter((x) => HIST_GROUP[x.kind] === hist);
  useEffect(() => {
    if (!openTicket && !openLead) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [openTicket, openLead]);

  const openLeadId = openLead?.leadId || '';
  useEffect(() => {
    if (!openLeadId) { setLeadLog(null); return; }
    let live = true;
    setLeadLog(null);
    fetch(`${BACKEND_URL}/api/ceo/office/leads/${encodeURIComponent(openLeadId)}/log`,
      { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { log: [] }))
      .then((d) => { if (live) setLeadLog(d.log || []); })
      .catch(() => { if (live) setLeadLog([]); });
    // `leads` is in here on purpose: a stage change reloads the list, and the history under the
    // panel has to gain the row that change just wrote — otherwise the panel shows an edit that
    // its own log does not, which is worse than no log at all.
    return () => { live = false; };
  }, [openLeadId, leads]);

  const leadRows = useMemo(() => {
    let all = leads?.leads || [];
    const q = lsearch.trim().toLowerCase();
    if (q) {
      // Word-wise over the counterparty and the id, the same reading the ticket box gives.
      const words = q.split(/\s+/).filter(Boolean);
      all = all.filter((r) => {
        const hay = [r.counterparty, r.leadId].filter(Boolean).join(' ').toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }
    const afterCols = Object.keys(lcols).length
      ? all.filter((r) => rowPassesColumnFilters(r, lcols, LEAD_GETTERS)) : all;
    return lsort.key ? sortRows(afterCols, lsort, LEAD_SORTERS) : afterCols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, lcols, lsort, lsearch]);

  /* THE OPEN TICKET'S FILES, fetched when the panel opens. Cleared first for the reason the lead
     log is: a panel showing the previous ticket's attachments while the request is in flight is
     the worst possible moment to be looking at somebody else's documents. */
  const openTicketId = openTicket?.ticket?.ticketId || '';
  /* ONE OWNER AT A TIME. A ticket and a lead are never both open — the tabs see to that — so one
     piece of state serves both, and it carries the key so a stale response cannot paint the wrong
     panel. Max, 2026-09-23: "bên lead cũng cần có attach file nhé". */
  const fileOwner = openTicketId
    ? { key: openTicketId, path: 'tickets' }
    : (openLeadId ? { key: openLeadId, path: 'leads' } : null);
  const fileKey = fileOwner?.key || '';
  useEffect(() => {
    if (!fileOwner) { setFiles({ key: '', list: [] }); return undefined; }
    let live = true;
    setFiles({ key: fileOwner.key, list: [] });
    fetch(`${API}/${fileOwner.path}/${encodeURIComponent(fileOwner.key)}/files`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { files: [] }))
      .then((d) => { if (live) setFiles({ key: fileOwner.key, list: d.files || [] }); })
      .catch(() => { if (live) setFiles({ key: fileOwner.key, list: [] }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey]);

  /* THE FILE IS READ IN THE BROWSER and posted as base64, which is how every other upload in this
     portal works — one shape, and no multipart parser to add. `onerror` matters: a file the browser
     cannot read has to say so rather than leaving a button spinning. */
  const addFile = async (file) => {
    if (!file || !fileOwner) return;
    setUpBusy(file.name); setError('');
    try {
      const dataBase64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(new Error(t('ceo.fileUnreadable')));
        fr.readAsDataURL(file);
      });
      const res = await fetch(`${API}/${fileOwner.path}/${encodeURIComponent(fileOwner.key)}/files`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, dataBase64 }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xCeo.attachFailed'));
      setFiles({ key: fileOwner.key, list: d.files || [] });
      /* The upload writes an event, so the log under it has to be re-read or it disagrees with the
         list above it. A lead's log hangs off `leads`, which `loadLeads` replaces. */
      if (openTicketId) await openOne(openTicketId); else await loadLeads();
    } catch (e) { setError(e.message); } finally { setUpBusy(''); }
  };

  /* ASKED IN THE PORTAL'S OWN DIALOG — Max, 2026-09-23: "thêm modal cho xóa tài liệu".
     `window.confirm` put "helinoxvn.com:3000 says" above the question, in the browser's chrome,
     over a screen it knows nothing about — it cannot show the file's size or who uploaded it, and
     it reads as a warning from the browser rather than a question from the portal. It also blocks
     the whole tab, which is the wrong behaviour for a step somebody may well want to back out of
     after looking at the row again. */
  const removeFile = async (f) => {
    setDropFile(null);
    setUpBusy(f.name); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/development/photos/file/${f.fileId}`,
        { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || t('xCeo.removeFailed'));
      setFiles((prev) => ({ ...prev, list: prev.list.filter((x) => x.fileId !== f.fileId) }));
    } catch (e) { setError(e.message); } finally { setUpBusy(''); }
  };

  /* ARRIVING FROM THE 08:30 MAIL — Max, 2026-09-22: "khi click vào thì sẽ navigate đến portal thì
     cần highlight cái phiếu đó lên á". Every id in that mail is a link, and a link that lands on a
     table of thirty rows with nothing lit has answered "open the portal" and not "look at this one".

     It OPENS the panel as well as lighting the row, because the mail was about that ticket and the
     panel is where its story is; the light is what says WHICH row the panel came from.

     THE PARAMETER IS CONSUMED. A reload an hour later must not relight a row nobody is looking at,
     and the calculator already settled this shape (`?open=` is stripped with replaceState). The
     light itself clears after six seconds — the same six the Project Approval row path uses, long
     enough to find and short enough not to become part of the furniture.

     IT SITS HERE, below `tickets` and `leadRows`, deliberately: the Project Approval version of
     this crashed the whole screen on its first build by being written above the list it reads
     ("Cannot access 'm' before initialization"). */
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const tk = q.get('ticket');
    const ld = q.get('lead');
    if (!tk && !ld) return;
    if (!data && !leads) return;          // wait for the rows, or there is nothing to scroll to
    if (tk) { setTab('tickets'); setHit(tk); openOne(tk); }
    else { setTab('leads'); setHit(ld); }
    q.delete('ticket'); q.delete('lead');
    const rest = q.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, leads]);

  /* Scroll to it once it is actually drawn, and let go after six seconds. The row may not exist —
     a mail from last week can point at a ticket somebody deleted — so nothing here assumes it
     found one. */
  useEffect(() => {
    if (!hit) return undefined;
    const el = document.querySelector(`[data-row="${CSS.escape(hit)}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const timer = setTimeout(() => setHit(''), 6000);
    return () => clearTimeout(timer);
  }, [hit, tickets, leadRows]);

  /* ONE header-prop builder for both tables, because they behave the same and two copies is how a
     funnel comes to clear on one screen and stick on the other. */
  const th = (key, base, setBase, colsState, setCols, openState, setOpen, getters, rowsBefore) => ({
    sortKey: key,
    sort: base,
    onSort: (k) => setBase((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' })),
    filterValues: openState?.key === key ? distinctColumnValues(rowsBefore, key, colsState, getters) : undefined,
    filterSelected: colsState[key],
    filterOpen: openState?.key === key,
    filterAnchor: openState?.key === key ? openState.anchor : null,
    onOpenFilter: (k, anchor) => setOpen((prev) => (prev && prev.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpen(null),
    onApplyFilter: (k, values, allValues) => {
      setCols((prev) => {
        const next = { ...prev };
        // Everything ticked is the same as no filter — keeping it would light the funnel for ever.
        const isAll = allValues && allValues.length && values.length >= allValues.length;
        if (!values.length || isAll) delete next[k]; else next[k] = values;
        return next;
      });
      setOpen(null);
    },
  });
  /* WHAT IS NARROWING THE TABLE, and the one way out — Max, 2026-09-22: "chưa có nút clear sort
     hay filter". The Tickets Reset existed but said nothing about what it would clear, and the
     Leads table had none at all: a column funnel is a filter you cannot see from anywhere but the
     column it sits on, so a table narrowed by one somebody set yesterday is the portal's own rule
     about a list narrowed by something nothing on screen mentions. */
  const narrowing = (cardKey, q, colsState, sortState) => [
    cardKey ? t(`ceo.${CARDS.find((c) => c.key === cardKey).tk}`) : null,
    q ? `"${q}"` : null,
    ...Object.entries(colsState).map(([k, v]) => `${t(CEO_COL_LABEL[k] || LEAD_COL_LABEL[k] || k)}: ${v.join(', ')}`),
    sortState.key ? t('ceo.sortedBy', { col: t(CEO_COL_LABEL[sortState.key] || LEAD_COL_LABEL[sortState.key] || sortState.key) }) : null,
  ].filter(Boolean);

  const tkNarrow = narrowing(card, search, cols, sort);
  const ldNarrow = narrowing('', lsearch, lcols, lsort);

  const tkTh = (key) => th(key, sort, setSort, cols, setCols, openFilter, setOpenFilter, CEO_GETTERS, data?.tickets || []);
  const ldTh = (key) => th(key, lsort, setLsort, lcols, setLcols, lopenFilter, setLopenFilter, LEAD_GETTERS, leads?.leads || []);

  /* THE TYPE LIST IS WHAT THE TICKETS HOLD — Max, 2026-09-22: a dropdown of the values in use, and
     a new one addable the way the tracker's Partner / Line works. No dim table behind it: the
     options ARE the distinct types on the tickets, so choosing a new one puts it on this ticket and
     it is in the list for the next. A table would need a screen to manage it and would drift from
     what the tickets actually say the first time somebody deleted a row that was in use.

     `ListPicker` is the tracker's own component, so the interaction is the one people already know
     — search what exists, and adding is a deliberate second click that folds anything differing
     only by case or spacing onto the spelling already in use. That folding is the point: free text
     let "Follow-up" / "follow up" / "Followup" become three types that are really one. */
  const typeOptions = useMemo(
    () => [...new Set((data?.tickets || []).map((r) => r.type).filter(Boolean))].sort(),
    [data],
  );

  const desks = data?.desks || [];

  /* THE DESK COLUMN SHOWS WHEN THE DESK IS IN QUESTION. It used to be `desk === ''` — hidden the
     moment a desk was chosen, which is right, because every row then has the same desk. The queue
     broke that reading: 'unassigned' is not a desk, it is the rows that have none, and the column
     is where each of them is GIVEN one. Hiding it there took away the only control on the pile.
  */
  /* AN INTAKE PERSON ON ONE DESK STILL PLACES ON EVERY DESK — Sheryl, 2026-11-01: "my CEO Office screen does not show
     the DESK row … so I cannot place tickets on Business or Executive". She is intake and on Executive only, and both the
     bar and the column waited for two desks of her own, so the queue had no control at all. The server lets intake place
     on any desk (POST /tickets/:id/desk); the picker already lists every desk. Somebody on one desk who is NOT intake
     still gets no switcher — that rule stands. */
  const deskBar = desks.length > 1 || !!data?.isIntake;
  const showDeskCol = (desk === '' || desk === 'unassigned') && deskBar;

  // The addresses are a setting for the section, not a ticket, so they follow the edit grant.
  const canEditEntities = !!data;

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main ceo-page">
        <div className="mh-titlerow">
          <div className="sp-title">
            <h2>{t('ceo.title')} · {t(`ceo.tab_${tab}`)}</h2>
            <p>{t(`ceo.sub_${tab}`)}</p>
          </div>
          <ModuleHelp manual="ceo" steps={TOUR} tourKey="ceoOffice" ready={!!data} onBeforeTour={() => setTab('tickets')} />
        </div>

        {/* THREE SCREENS, as Young drew them, each carrying its own count so the bar says how much
            is behind a tab before it is opened. */}
        <div className="ceo-tabs view-tabs" role="tablist">
          {[['tickets', (data?.tickets || []).filter((x) => !x.restricted).length],
            ['leads', leads?.counters?.openTotal ?? null],
            ['daily', null]].map(([k, n]) => (
            <button
              key={k}
              type="button"
              className={`ceo-tab${tab === k ? ' on' : ''}`}
              aria-pressed={tab === k}
              onClick={() => setTab(k)}
            >
              <TabIcon k={k} />{t(`ceo.tab_${k}`)}{n === null ? '' : <i>{n}</i>}
            </button>
          ))}
        </div>

        {!!error && <div className="sp-alert err">{error}</div>}

        {/* THE DESK BAR IS THE SECTION'S, not one screen's — but Leads sit on the business desk
            only (Young's mockup says so under its own table), so it has nothing to switch. */}
        <div className="ceo-bar" hidden={tab === 'leads'}>
          {/* NO "ALL DESKS" BUTTON. The server hands back only the desks this caller holds, so a
              person on one desk gets no switcher rather than one that names the other. */}
          {/* THE PILLS CARRY WHOSE QUEUE AND HOW BIG — Young's mockup: "Business · Evonne 28",
              not a bare desk name. And the sentence beside them, which is the rule of the whole
              section said once where somebody will read it. */}
          {deskBar && tab !== 'leads' && (
            <div className="ceo-desks">
              <span>{t('ceo.desk')}</span>
              {/* THE SAME NUMBER AS THE TAB — Max, 2026-09-22: "tôi thấy có 10 ticket mà all ở
                  dưới thì hiện 8". It did: the tab counted every ticket and this counted only the
                  open ones. Two counts of different things, side by side, with nothing saying so.
                  Both count TICKETS now, which is what Young's mockup does (Tickets 41 · All 41);
                  how many are still open is the IN PROGRESS card's job, and it says so. */}
              {/* ALL IS THE SUM OF THE PILLS BESIDE IT — Jay, 2026-09-23: "the desk counts look off.
                  The screen shows All 20, Business 20, Executive 10". It did: this counted
                  `data.tickets`, and picking a desk makes the server send only THAT desk's rows,
                  so All became whatever was selected. `deskMeta` is counted server-side from
                  everything the caller may see and does not move when the filter does — the same
                  correction the two desk pills themselves needed yesterday, missed here because
                  this one is written out by hand rather than mapped. */}
              <button type="button" className={`ceo-dbtn${desk === '' ? ' on' : ''}`} onClick={() => setDesk('')}>
                {t('ceo.allDesks')}
                {/* THE QUEUE COUNTS TOWARDS ALL. deskMeta counts PER DESK, and an unassigned item
                    is on none — so All read 0 while a row sat on the screen under it, which is
                    the same fault Jay caught in September for a different reason. */}
                <i>{(data?.deskMeta || []).filter((m) => m.mine).reduce((n, m) => n + m.count, 0)
                   + (data?.isIntake ? (data.queueCount || 0) : 0)}</i>
              </button>
              {/* THE QUEUE SITS BESIDE THE DESKS, NOT INSIDE ONE — Young, point 2. It is drawn
                  from `isIntake`, not from whether there happen to be rows in it: a section that
                  appears and vanishes with its own contents leaves an intake person with an empty
                  queue unable to tell "nothing waiting" from "no such thing". Somebody who is not
                  intake never sees the pill at all, which is the rest of his sentence. */}
              {data?.isIntake && (
                <button
                  type="button"
                  className={`ceo-dbtn ceo-dbtn-queue${desk === 'unassigned' ? ' on' : ''}`}
                  onClick={() => setDesk(desk === 'unassigned' ? '' : 'unassigned')}
                >
                  {t('ceo.unassigned')}<i>{data.queueCount || 0}</i>
                </button>
              )}
              {(data?.deskMeta || []).filter((m) => m.mine).map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`ceo-dbtn${desk === m.key ? ' on' : ''}`}
                  onClick={() => setDesk(m.key)}
                >
                  {t(`ceo.desk_${m.key}`)}{m.owners.length ? ` · ${m.owners.join(', ')}` : ''}<i>{m.count}</i>
                </button>
              ))}
              <em className="ceo-deskrule">{t('ceo.deskRule')}</em>
            </div>
          )}
          <span className="ceo-spacer" />
          <button type="button" className="sp-btn" disabled={busy} onClick={() => load(desk)}>
            <RefreshCw size={13} /> {t('ceo.refresh')}
          </button>
          {/* Raising a TICKET from beside a table of LEADS is the wrong button in the wrong
              place; the leads table carries its own New lead. */}
          {tab !== 'leads' && (
            <button
              type="button"
              className="sp-btn sp-btn-primary"
              disabled={busy || !desks.length}
              onClick={() => setAdding({ desk: desk || desks[0], title: '', status: 'Open' })}
            >
              <Plus size={13} /> {t('ceo.newTicket')}
            </button>
          )}
        </div>

        {tab === 'tickets' && (<>
        <div className="ceo-cards">
          {CARDS.map((c) => (
            <button
              type="button"
              key={c.key}
              className={`ceo-card ${c.tone}${card === c.key ? ' on' : ''}`}
              aria-pressed={card === c.key}
              onClick={() => setCard(card === c.key ? '' : c.key)}
            >
              {/* The mockup's order: the NAME first in its own colour, the figure under it, the
                  rule under that. Reading it top to bottom is "what this is, how many, how it is
                  counted" — the figure alone answers none of those. */}
              <span>{t(`ceo.${c.tk}`)}</span>
              <b>{data?.counters?.[c.key] ?? '—'}</b>
              <em>{t(`ceo.${c.tk}Rule`)}</em>
            </button>
          ))}
        </div>

        <div className="ceo-filters">
          {/* The five dropdowns are GONE — every one of them is a column, and the column's own
              funnel filters it (Max, 2026-09-22). What stays is the SEARCH, which is not a column:
              it reads the title and the sender together. */}
          <input
            type="search"
            className="ceo-search"
            placeholder={t('ceo.searchPh')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="sp-btn"
            disabled={!tkNarrow.length}
            title={tkNarrow.length ? tkNarrow.join(' · ') : undefined}
            onClick={() => { setSearch(''); setCard(''); setCols({}); setSort({ key: '', dir: 'asc' }); }}
          >
            {tkNarrow.length ? t('ceo.resetN', { n: tkNarrow.length }) : t('ceo.reset')}
          </button>
          <span className="ceo-spacer" />
          {/* The same endpoint the Project Tracker and Ticket Station export through — it takes
              columns, rows and widths and knows nothing about which screen sent them. */}
          <button type="button" className="sp-btn" disabled={busy || !tickets.length} onClick={exportXlsx}>
            {t('ceo.exportExcel')}
          </button>
        </div>

        {/* A TABLE NARROWED BY SOMETHING NOTHING ON SCREEN MENTIONS is the same as a broken table —
            the rule this portal already keeps. The card that narrowed it is named, with the way
            out beside it. */}
        {!!tkNarrow.length && (
          <p className="ceo-filtered">
            <b>{t('ceo.showingN', { n: tickets.length, total: (data?.tickets || []).length })}</b>
            {tkNarrow.map((x) => <i key={x}>{x}</i>)}
            <button type="button" onClick={() => { setSearch(''); setCard(''); setCols({}); setSort({ key: '', dir: 'asc' }); }}>
              {t('ceo.clearFilter')}
            </button>
          </p>
        )}

        <div className="sp-table-wrap ceo-wrap">
          <table className="sp-table ceo-table">
            <thead>
              <tr>
                {/* The mockup numbers its rows. It is not the ticket id — it is where you are in
                    the list, which is what somebody reading down a filtered table refers to. */}
                <th className="ceo-num">#</th>
                <SortTh label={t('ceo.colTicket')} {...tkTh('ticketId')} />
                <SortTh label={t('ceo.colTitle')} {...tkTh('title')} />
                {showDeskCol && <SortTh label={t('ceo.colDesk')} {...tkTh('desk')} />}
                <SortTh label={t('ceo.colType')} {...tkTh('type')} />
                <SortTh label={t('ceo.colEntity')} {...tkTh('entity')} />
                <SortTh label={t('ceo.colContact')} {...tkTh('internalContact')} />
                <SortTh label={t('ceo.colOwner')} {...tkTh('owner')} />
                <SortTh label={t('ceo.colDue')} {...tkTh('dueDate')} />
                {/* BESIDE THE DUE DATE — Jay, 2026-09-23: "my only final comment is to move the
                    'D-Day' column next to the 'Due' column." They are one fact twice: the date
                    that was promised and how far from it today is, so reading one without the
                    other was always a sideways glance across four columns.
                    THE SIGN, SAID OUT LOUD, stays with it. The portal's convention is POSITIVE IS
                    OVERDUE — set on 2026-08-12 and read that way on the Project Tracker and the
                    Sample Tracker every day since — and two conventions inside one portal is worse
                    than one mockup being redrawn. So it is labelled: the header's hint, and a line
                    under the table, because a hint is only found by somebody who already
                    suspects. */}
                <SortTh label={t('ceo.colDday')} hint={t('ceo.ddayWhat')} className="num" {...tkTh('dDay')} />
                <SortTh label={t('ceo.colStatus')} {...tkTh('status')} />
                {/* D-DAY AND THIS ARE NOT THE SAME MEASUREMENT and the table needs both. D-day
                    measures a ticket against the date somebody promised; this measures it against
                    the last time anything happened to it. A ticket 11 days past its date is a
                    person running late; one sitting 11 days at "Waiting on CEO" is the CEO running
                    late, and nothing on this screen told those two apart. */}
                <SortTh label={t('ceo.colDwell')} {...tkTh('daysAtStatus')} />
                <SortTh label={t('ceo.colLast')} {...tkTh('lastContactAt')} />
                <th />
              </tr>
            </thead>
            <tbody>
              {!tickets.length && (
                <tr><td colSpan={14} className="ceo-empty">
                  {desks.length ? t('ceo.noTickets') : t('ceo.noDesk')}
                </td></tr>
              )}
              {tickets.map((r, i) => (
                <tr
                  key={r.ticketId}
                  data-row={r.ticketId}
                  className={`${openTicket?.ticket?.ticketId === r.ticketId ? 'on' : ''}${r.restricted ? ' ceo-rowoff' : ''}${hit === r.ticketId ? ' hit' : ''}`}
                >
                  <td className="ceo-num">{i + 1}</td>
                  <td>
                    {/* A REDACTED ROW IS NOT A LINK. Its existence is on the screen (Max,
                        2026-09-22) but its log is the other desk's, so there is nothing behind the
                        id to open — and a link that answers 403 is a screen offering something it
                        knows will be refused. */}
                    {r.restricted
                      ? <span className="ceo-id off" title={t('ceo.otherDesk')}>{r.ticketId}</span>
                      : <button type="button" className="ceo-id" onClick={() => openOne(r.ticketId)}>{r.ticketId}</button>}
                  </td>
                  <td className="ceo-titlecell">
                    <b>{r.title}</b>
                    {/* `sender` comes back NULL for the other desk — the one field Young's mockup
                        replaces. Printed as the word, not as a blank, so the row says it is
                        withholding something rather than looking like a ticket nobody addressed. */}
                    {r.restricted
                      ? <span className="ceo-restricted">{t('ceo.restricted')}</span>
                      : (!!r.sender && <span>{r.sender}</span>)}
                  </td>
                  {showDeskCol && (
                    <td>{r.desk ? (
                      <i className={`ceo-deskchip ${r.desk}`}>{t(`ceo.desk_${r.desk}`)}</i>
                    ) : (
                      /* THE CELL THAT NAMES THE DESK IS WHERE THE DESK IS CHOSEN. One control, no
                         dialog: the person is reading the row to decide, and the answer belongs
                         next to what they just read. It saves on CHANGE, because there is nothing
                         else to fill in — a Save beside it would be a second click for one value. */
                      <select
                        className="ceo-deskpick"
                        value=""
                        disabled={placing === r.ticketId}
                        onChange={(e) => placeOnDesk(r, e.target.value)}
                      >
                        <option value="">{t('ceo.choosedesk')}</option>
                        {(data?.deskMeta || []).map((m) => (
                          <option key={m.key} value={m.key}>{t(`ceo.desk_${m.key}`)}</option>
                        ))}
                      </select>
                    )}</td>
                  )}
                  <td>{r.type || <span className="ceo-dash">—</span>}</td>
                  {/* Empty is the normal state (rule 3), so it is named rather than left blank —
                      a blank cell reads as an oversight and this one is a stage. */}
                  <td>{r.entity || <span className="ceo-unset">{t('ceo.notSet')}</span>}</td>
                  <td>{r.internalContact || <span className="ceo-unset">{t('ceo.notSet')}</span>}</td>
                  <td>{r.owner || <span className="ceo-dash">—</span>}</td>
                  <td>{fmtDate(r.dueDate) || <span className="ceo-dash">—</span>}</td>
                  {/* "+5d", "−3d", "—" — the mockup's own shape. A bare number says nothing
                      about which side of the date it is on, and this portal's convention (positive
                      is overdue) is the opposite of what most people assume from "D-day". */}
                  <td className={`num ${(r.dDay ?? -1) > 0 ? 'ceo-over' : ''}`}>
                    {r.dDay === null || r.dDay === undefined
                      ? <span className="ceo-dash">—</span>
                      : `${r.dDay > 0 ? '+' : (r.dDay < 0 ? '−' : '')}${Math.abs(r.dDay)}d`}
                  </td>
                  <td><i className={`ceo-status s-${String(r.status).toLowerCase().replace(/\s+/g, '-')}`}
                    title={r.status === 'Closed' && r.closedBy
                      ? t('ceo.closedBy', { who: r.closedBy, when: fmtWhen(r.closedAt) }) : undefined}>{r.status}</i></td>
                  <td className="ceo-dwell">
                    {r.daysAtStatus === null || r.daysAtStatus === undefined
                      ? <span className="ceo-dash">—</span>
                      /* Loud only once it is long enough to mean something. Every open ticket has
                         a number here, so colouring them all would colour the column. */
                      : <b className={r.daysAtStatus >= 7 ? 'hot' : ''}>{t('ceo.nDays', { n: r.daysAtStatus })}</b>}
                  </td>
                  {/* Red once it is three days old — the same rule the UNTOUCHED card counts by,
                      so the column and the card can never point at different rows. */}
                  <td className={staleContact(r) ? 'ceo-over' : ''}>
                    {fmtDate(r.lastContactAt) || <span className="ceo-dash">—</span>}
                  </td>
                  <td className="ceo-open">
                    {!r.restricted && (
                      <button type="button" className="sp-btn" onClick={() => openOne(r.ticketId)}>
                        {t('ceo.open')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* THE LOG, which Young called the whole point of the section. It is read-only and in one
            direction: newest first, every row saying who did what and when. */}
        {/* A POPUP, not a panel below — Jay and Max both, 2026-09-23. I had argued for the panel
            on the grounds that every other detail view in this portal opens below its table; two
            people who use the screen asked for the popup, which settles it.
            It CLOSES ON THE BACKDROP and on Escape, and the body scrolls inside it: the ticket
            carries eight fields, its attachments and its whole event log, which on a short laptop
            is taller than the window. `stopPropagation` on the card, or every click inside it
            would close the thing being read. */}
        {openTicket && (
          <div className="ceo-modalback" onClick={() => setOpenTicket(null)}>
          <div className="ceo-panel" onClick={(e) => e.stopPropagation()}>
            <div className="ceo-panel-head">
              <div>
                <b>{openTicket.ticket.ticketId}</b>
                <span>{openTicket.ticket.title}</span>
              </div>
              <div className="ceo-panel-acts">
                {openTicket.ticket.status === 'Closed' ? (
                  <button type="button" className="sp-btn" disabled={busy} data-testid="ceo-reopen"
                    onClick={() => setClosing({ id: openTicket.ticket.ticketId, mode: 'reopen', note: '' })}>
                    <RotateCcw size={13} /> {t('ceo.reopenTicket')}
                  </button>
                ) : (
                  <button type="button" className="sp-btn ceo-closebtn" disabled={busy} data-testid="ceo-close"
                    onClick={() => setClosing({ id: openTicket.ticket.ticketId, mode: 'close', note: '' })}>
                    <CheckCircle2 size={13} /> {t('ceo.closeTicket')}
                  </button>
                )}
                <button type="button" className="ceo-x" onClick={() => setOpenTicket(null)} aria-label={t('ceo.close')}>
                  <X size={15} />
                </button>
              </div>
            </div>
            {openTicket.ticket.status === 'Closed' && (
              <div className="ceo-closedbar" data-testid="ceo-closedbar">
                <CheckCircle2 size={14} />
                <span>{t('ceo.closedBy', {
                  who: openTicket.ticket.closedBy || '—',
                  when: fmtWhen(openTicket.ticket.closedAt),
                })}</span>
                {!!openTicket.ticket.closeNote && <q>{openTicket.ticket.closeNote}</q>}
              </div>
            )}

            {/* EVERYTHING THE TICKET SAYS, editable — Max, 2026-09-22: "trước hợp muốn sửa lại
                thông tin của ticket thì làm sao". It could not be done: the panel offered status,
                entity, contact, owner and due, and the three things a ticket most often arrives
                WRONG in — its title, what kind of thing it is, and who it came from — could only be
                set when it was raised. A ticket made from a forwarded mail gets its title from the
                subject line, which is exactly the text somebody will want to fix. */}
            <div className="ceo-edit">
              <label className="wide"><span>{t('ceo.fTitle')}</span>
                <input
                  defaultValue={openTicket.ticket.title}
                  key={`ti-${openTicket.ticket.ticketId}`}
                  onBlur={(e) => e.target.value.trim() && e.target.value !== openTicket.ticket.title
                    && save(openTicket.ticket.ticketId, { title: e.target.value })}
                />
              </label>
              <label><span>{t('ceo.fromWho')}</span>
                <input
                  defaultValue={openTicket.ticket.sender}
                  key={`se-${openTicket.ticket.ticketId}`}
                  onBlur={(e) => e.target.value !== openTicket.ticket.sender
                    && save(openTicket.ticket.ticketId, { sender: e.target.value })}
                />
              </label>
              <label><span>{t('ceo.colType')}</span>
                <ListPicker
                  value={openTicket.ticket.type}
                  options={typeOptions}
                  placeholder={t('ceo.fTypePh')}
                  onChange={(v) => v !== openTicket.ticket.type
                    && save(openTicket.ticket.ticketId, { type: v })}
                />
              </label>
              <label><span>{t('ceo.colStatus')}</span>
                {/* CLOSED IS NOT IN THE LIST — it is the Close button's, which takes a note and
                    says who closed it; two ways to close is two histories of what closing means.
                    A closed ticket's status is changed by Reopen, so the picker waits. */}
                <select
                  value={openTicket.ticket.status}
                  disabled={openTicket.ticket.status === 'Closed'}
                  title={openTicket.ticket.status === 'Closed' ? t('ceo.statusClosedHint') : undefined}
                  onChange={(e) => save(openTicket.ticket.ticketId, { status: e.target.value })}
                >
                  {(openTicket.ticket.status === 'Closed' ? STATUSES : STATUSES.filter((s) => s !== 'Closed'))
                    .map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label><span>{t('ceo.colEntity')}</span>
                <select
                  value={openTicket.ticket.entity || ''}
                  onChange={(e) => save(openTicket.ticket.ticketId, { entity: e.target.value })}
                >
                  <option value="">{t('ceo.notSet')}</option>
                  {(data?.entities || []).map((x) => <option key={x.code} value={x.code}>{x.code}</option>)}
                </select>
              </label>
              <label><span>{t('ceo.colContact')}</span>
                <input
                  defaultValue={openTicket.ticket.internalContact}
                  onBlur={(e) => e.target.value !== openTicket.ticket.internalContact
                    && save(openTicket.ticket.ticketId, { internalContact: e.target.value })}
                />
              </label>
              <label><span>{t('ceo.colOwner')}</span>
                <input
                  defaultValue={openTicket.ticket.owner}
                  onBlur={(e) => e.target.value !== openTicket.ticket.owner
                    && save(openTicket.ticket.ticketId, { owner: e.target.value })}
                />
              </label>
              <label><span>{t('ceo.colDue')}</span>
                <input
                  type="date"
                  defaultValue={fmtDate(openTicket.ticket.dueDate)}
                  onBlur={(e) => e.target.value !== fmtDate(openTicket.ticket.dueDate)
                    && save(openTicket.ticket.ticketId, { dueDate: e.target.value || null })}
                />
              </label>
              {/* A CONTACT IS AN EVENT, not a date field — that is what makes "untouched 3+ days"
                  a measurement rather than something somebody keeps up to date. */}
              {/* TWO BUTTONS THAT WRITE A DATE NOBODY TYPES — Max asked what they are for, which
                  means a reader will too. They stamp a contact EVENT on the ticket, and that event
                  is what Last contact prints and what UNTOUCHED 3+ DAYS counts from; the two are
                  separate because "we chased them" and "they answered" are different facts about
                  the same silence. The label above them says it rather than leaving two verbs on
                  a panel to be guessed at. */}
              <div className="ceo-contactbtns wide">
                <span className="ceo-contacthint">{t('ceo.contactHint')}</span>
                <button type="button" className="sp-btn" disabled={busy}
                  title={t('ceo.logContactedWhy')}
                  onClick={() => setLogging({ id: openTicket.ticket.ticketId, kind: 'contacted', channel: '', note: '' })}>
                  <MessageSquare size={13} /> {t('ceo.logContacted')}
                </button>
                <button type="button" className="sp-btn" disabled={busy}
                  title={t('ceo.logReplyWhy')}
                  onClick={() => setLogging({ id: openTicket.ticket.ticketId, kind: 'reply', channel: '', note: '' })}>
                  {t('ceo.logReply')}
                </button>
                {/* NOT A CONTACT — it moves no date and no counter, so it sits apart from the two
                    that do. */}
                <button type="button" className="sp-btn ceo-notebtn" disabled={busy} data-testid="ceo-add-note"
                  title={t('ceo.logNoteWhy')}
                  onClick={() => setLogging({ id: openTicket.ticket.ticketId, kind: 'note', channel: '', note: '' })}>
                  <StickyNote size={13} /> {t('ceo.logNote')}
                </button>
              </div>
            </div>

            {/* ATTACHMENTS — Max, 2026-09-23. The status list has carried `Awaiting files` since
                yesterday and there was nowhere to put one when it arrived: a state the screen
                could enter and not leave by its own means.
                It sits ABOVE the log and below the buttons, because it is part of the ticket and
                the log is the story of the ticket. */}
            {filesBlock()}

            {/* HISTORY — Young, 2026-10-20. Three kinds of thing, drawn three ways so they cannot be
                mistaken for each other: an EMAIL is a card with its sender, recipients and words; a
                NOTE (or a contact the desk recorded by hand) is a tinted box; a CHANGE is one grey
                line. Oldest first, because "chronological order" is what was asked for and a
                conversation reads top to bottom. */}
            <div className="ceo-histhead">
              <h4>{t('ceo.historyTitle')}</h4>
              <div className="ceo-histchips" role="tablist">
                {['all', 'mail', 'note', 'change'].map((k) => (
                  <button key={k} type="button" role="tab" aria-selected={hist === k}
                    className={`ceo-histchip${hist === k ? ' on' : ''}`} data-testid={`ceo-hist-${k}`}
                    onClick={() => setHist(k)}>
                    {t(`ceo.hist_${k}`)} <i>{histCounts[k]}</i>
                  </button>
                ))}
              </div>
            </div>
            <ol className="ceo-hist" data-testid="ceo-history">
              {!histShown.length && <li className="ceo-histempty">{t('ceo.historyEmpty')}</li>}
              {histShown.map((x) => {
                if (x.kind === 'mail') {
                  const m = x.mail;
                  const [fresh, older] = splitQuoted(m.body);
                  const open = quoted.has(m.id);
                  return (
                    <li key={x.key} className={`ceo-hmail ${m.direction}`} data-kind="mail">
                      <div className="ceo-hhead">
                        <i className={`ceo-hk ${m.direction}`} title={t(m.direction === 'out' ? 'ceo.hSentWhy' : 'ceo.hReceivedWhy')}>
                          <Mail size={12} /> {t(m.direction === 'out' ? 'ceo.hSent' : 'ceo.hReceived')}
                        </i>
                        <b>{m.fromName || m.from}</b>
                        {!!m.fromName && <span className="ceo-haddr">{m.from}</span>}
                        <span className="ceo-evat">{fmtWhen(m.at)}</span>
                      </div>
                      <div className="ceo-hmeta"><span>{t('ceo.hTo')}</span>{m.to || '—'}</div>
                      {!!m.cc && <div className="ceo-hmeta"><span>{t('ceo.hCc')}</span>{m.cc}</div>}
                      {!!m.subject && <div className="ceo-hsubj">{m.subject}</div>}
                      <div className="ceo-hbody">{fresh || older}</div>
                      {!!fresh && !!older && (
                        <>
                          <button type="button" className="ceo-hquote" aria-expanded={open}
                            onClick={() => setQuoted((q) => {
                              const n = new Set(q);
                              if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
                              return n;
                            })}>
                            {t(open ? 'ceo.hQuotedHide' : 'ceo.hQuoted')}
                          </button>
                          {open && <div className="ceo-hbody ceo-hold">{older}</div>}
                        </>
                      )}
                      {!!m.attachments && (
                        <div className="ceo-hatt"><Paperclip size={12} /> {m.attachments}</div>
                      )}
                    </li>
                  );
                }
                const l = x.log;
                if (x.kind === 'note' || x.kind === 'contact') {
                  return (
                    <li key={x.key} className={`ceo-hnote ${x.kind}`} data-kind={x.kind}>
                      <div className="ceo-hhead">
                        <i className={`ceo-hk ${x.kind}`}>
                          {x.kind === 'note'
                            ? <><StickyNote size={12} /> {t('ceo.hNote')}</>
                            : t(l.event === 'reply' ? 'ceo.logReply' : 'ceo.logContacted')}
                        </i>
                        {x.kind === 'contact' && !!l.newValue && <i className="ceo-chan">{l.newValue}</i>}
                        <b>{l.by}</b>
                        <span className="ceo-evat">{fmtWhen(l.at)}</span>
                      </div>
                      {!!l.note && <div className="ceo-hbody">{l.note}</div>}
                    </li>
                  );
                }
                /* A CHANGE — one line, the field in the form's own words. */
                let what;
                if (l.event === 'created') {
                  what = l.by === 'mailbox' ? t('ceo.hCreatedMail') : t('ceo.hCreated');
                } else if (l.event === 'closed') {
                  what = t('ceo.hClosed', { from: l.oldValue || '—' });
                } else if (l.event === 'reopened') {
                  what = t('ceo.hReopened', { to: l.newValue || '—' });
                } else if (l.event === 'file') {
                  what = `${t('ceo.hFile')}: ${l.newValue || ''}`;
                } else if (l.field) {
                  what = `${t(FIELD_LABEL[l.field] || '', l.field)}: ${l.oldValue || '—'} → ${l.newValue || '—'}`;
                } else {
                  what = `${t(`ceo.ev_${l.event}`, l.event)}${l.newValue ? `: ${l.newValue}` : ''}`;
                }
                return (
                  <li key={x.key} className={`ceo-hchange e-${l.event}`} data-kind="change">
                    <span className="ceo-hwhat">
                      {(l.event === 'closed' || l.event === 'reopened')
                        ? <b className={`ceo-hk ${l.event}`}>{what}</b> : what}
                      {!!l.note && <q>{l.note}</q>}
                    </span>
                    <span className="ceo-evwho">{l.by === 'mailbox' ? '' : l.by}</span>
                    <span className="ceo-evat">{fmtWhen(l.at)}</span>
                  </li>
                );
              })}
            </ol>
            {/* WHAT THE MAILBOX CAN AND CANNOT SEE, said where the emails are, not in a mail. */}
            <p className="ceo-histscope">{t('ceo.historyScope', { mailbox: openTicket.mailbox || 'ceo.office@helinox.com' })}</p>
          </div>
          </div>
        )}

        <p className="ceo-legend">{t('ceo.ddayLegend')}</p>

        </>)}

        {/* ── LEADS ──────────────────────────────────────────────────────────────────────────
            The new-account pipeline, by stage. It has no desk switcher: leads sit on the business
            desk only, which Young states under this very table in his mockup. */}
        {tab === 'leads' && (
          <>
            <div className="ceo-cards ceo-leadcards">
              {(leads?.stages || []).map((st) => (
                <div className={`ceo-card ${LEAD_TONE[st] || ''}`} key={st}>
                  <span>{st}</span>
                  <b>{leads?.counters?.[st] ?? 0}</b>
                  <em>{t(`ceo.stageRule_${st.replace(/\s+/g, '_')}`, '')}</em>
                </div>
              ))}
            </div>

            <div className="ceo-filters">
              {/* The counterparty is what a lead is known by — it is the name somebody types when
                  they are looking for one, the way a ticket is looked for by its title. */}
              <input
                type="search"
                className="ceo-search"
                placeholder={t('ceo.searchLeadPh')}
                value={lsearch}
                onChange={(e) => setLsearch(e.target.value)}
              />
              <button
                type="button"
                className="sp-btn"
                disabled={!ldNarrow.length}
                title={ldNarrow.length ? ldNarrow.join(' · ') : undefined}
                onClick={() => { setLcols({}); setLsort({ key: '', dir: 'asc' }); setLsearch(''); }}
              >
                {ldNarrow.length ? t('ceo.resetN', { n: ldNarrow.length }) : t('ceo.reset')}
              </button>
              <span className="ceo-spacer" />
              <button type="button" className="sp-btn" disabled={busy || !leadRows.length}
                onClick={exportLeadsXlsx}>
                {t('ceo.exportExcel')}
              </button>
              <button type="button" className="sp-btn sp-btn-primary"
                onClick={() => setAddLead({ counterparty: '', stage: 'Contacted' })}>
                <Plus size={13} /> {t('ceo.newLead')}
              </button>
            </div>
            {!!ldNarrow.length && (
              <p className="ceo-filtered">
                <b>{t('ceo.showingN', { n: leadRows.length, total: (leads?.leads || []).length })}</b>
                {ldNarrow.map((x) => <i key={x}>{x}</i>)}
                <button type="button" onClick={() => { setLcols({}); setLsort({ key: '', dir: 'asc' }); setLsearch(''); }}>
                  {t('ceo.clearFilter')}
                </button>
              </p>
            )}

            <div className="sp-table-wrap ceo-wrap">
              <table className="sp-table ceo-table">
                <thead>
                  <tr>
                    <th className="ceo-num">#</th>
                    <SortTh label={t('ceo.colLead')} {...ldTh('leadId')} />
                    <SortTh label={t('ceo.colCounterparty')} {...ldTh('counterparty')} />
                    <SortTh label={t('ceo.colCountry')} {...ldTh('country')} />
                    <SortTh label={t('ceo.colSector')} {...ldTh('sector')} />
                    <SortTh label={t('ceo.colStage')} {...ldTh('stage')} />
                    <SortTh label={t('ceo.colCriteria')} {...ldTh('criteria')} />
                    <SortTh label={t('ceo.colLast')} {...ldTh('lastContactAt')} />
                    <SortTh label={t('ceo.colNext')} {...ldTh('nextContact')} />
                    <SortTh label={t('ceo.colTicket')} {...ldTh('ticketId')} />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {!leadRows.length && (
                    <tr><td colSpan={11} className="ceo-empty">{t('ceo.noLeads')}</td></tr>
                  )}
                  {leadRows.map((r, i) => (
                    <tr
                      key={r.leadId}
                      data-row={r.leadId}
                      className={`${openLead?.leadId === r.leadId ? 'on' : ''}${hit === r.leadId ? ' hit' : ''}`}
                    >
                      {/* THE TABLE READS AND THE PANEL EDITS — Max, 2026-09-22: "click phiếu thì
                          nội dung sẽ nằm ở dưới như bên ticket nhé". Every cell was its own control,
                          which made a wide table of half-height boxes and put six ways to change
                          something on one row; the ticket screen had already settled the shape. */}
                      <td className="ceo-num">{i + 1}</td>
                      <td>
                        <button type="button" className="ceo-id"
                          onClick={() => setOpenLead(openLead?.leadId === r.leadId ? null : r)}>{r.leadId}</button>
                      </td>
                      <td><b>{r.counterparty}</b></td>
                      <td>{r.country || <span className="ceo-dash">—</span>}</td>
                      <td>{r.sector || <span className="ceo-dash">—</span>}</td>
                      <td>
                        <i className={`ceo-stagepill s-${r.stage.toLowerCase().replace(/\s+/g, '-')}`}>{r.stage}</i>
                        {/* "No reply 4d" is DERIVED, so it rides beside the stage rather than
                            sitting in the list of stages somebody can pick. */}
                        {r.silentDays !== null && (
                          <i className="ceo-silent">{t('ceo.noReply', { n: r.silentDays })}</i>
                        )}
                      </td>
                      <td>{r.criteria || <span className="ceo-dash">—</span>}</td>
                      <td className={r.silentDays !== null ? 'ceo-over' : ''}>
                        {fmtDate(r.lastContactAt) || <span className="ceo-dash">—</span>}
                      </td>
                      <td>{fmtDate(r.nextContact) || <span className="ceo-dash">—</span>}</td>
                      <td>{r.ticketId
                        ? <button type="button" className="ceo-id"
                            onClick={() => { setTab('tickets'); openOne(r.ticketId); }}>{r.ticketId}</button>
                        : <span className="ceo-dash">—</span>}</td>
                      <td className="ceo-open">
                        <button type="button" className="sp-btn"
                          onClick={() => setOpenLead(openLead?.leadId === r.leadId ? null : r)}>
                          {t('ceo.open')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* THE SAME PANEL SHAPE AS A TICKET'S, below the table — one section, one way of
                opening a row. It carries no event log, and that is not an oversight: a lead has one
                contact stamp and no per-event history behind it. Worth a decision if the pipeline
                ever needs "when did it move to With CEO", which today nothing records. */}
            {openLead && (() => {
              const L = leadRows.find((x) => x.leadId === openLead.leadId) || openLead;
              return (
                <div className="ceo-modalback" onClick={() => setOpenLead(null)}>
                <div className="ceo-panel" onClick={(e) => e.stopPropagation()}>
                  <div className="ceo-panel-head">
                    <div>
                      <b>{L.leadId}</b>
                      <span>{L.counterparty}</span>
                    </div>
                    <button type="button" className="ceo-x" onClick={() => setOpenLead(null)} aria-label={t('ceo.close')}>
                      <X size={15} />
                    </button>
                  </div>

                  <div className="ceo-edit">
                    <label className="wide"><span>{t('ceo.colCounterparty')}</span>
                      <input
                        defaultValue={L.counterparty}
                        key={`cp-${L.leadId}`}
                        onBlur={(e) => e.target.value.trim() && e.target.value !== L.counterparty
                          && saveLead(L.leadId, { counterparty: e.target.value })}
                      />
                    </label>
                    <label><span>{t('ceo.colCountry')}</span>
                      <ListPicker
                        value={L.country}
                        options={[...new Set((leads?.leads || []).map((x) => x.country).filter(Boolean))].sort()}
                        placeholder={t('ceo.countryPh')}
                        onChange={(v) => v !== L.country && saveLead(L.leadId, { country: v })}
                      />
                    </label>
                    <label><span>{t('ceo.colSector')}</span>
                      <ListPicker
                        value={L.sector}
                        options={[...new Set((leads?.leads || []).map((x) => x.sector).filter(Boolean))].sort()}
                        placeholder={t('ceo.sectorPh')}
                        onChange={(v) => v !== L.sector && saveLead(L.leadId, { sector: v })}
                      />
                    </label>
                    <label><span>{t('ceo.colStage')}</span>
                      <select value={L.stage} onChange={(e) => saveLead(L.leadId, { stage: e.target.value })}>
                        {(leads?.stages || []).map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </label>
                    <label><span>{t('ceo.colNext')}</span>
                      <input
                        type="date"
                        defaultValue={fmtDate(L.nextContact)}
                        key={`nx-${L.leadId}`}
                        onBlur={(e) => e.target.value !== fmtDate(L.nextContact)
                          && saveLead(L.leadId, { nextContact: e.target.value || null })}
                      />
                    </label>
                    <label className="wide"><span>{t('ceo.colCriteria')}</span>
                      <input
                        defaultValue={L.criteria}
                        key={`cr-${L.leadId}`}
                        placeholder={t('ceo.criteriaPh')}
                        onBlur={(e) => e.target.value !== L.criteria
                          && saveLead(L.leadId, { criteria: e.target.value })}
                      />
                    </label>
                    <label className="wide"><span>{t('ceo.colTicket')}</span>
                      <ListPicker
                        value={L.ticketId}
                        options={(data?.tickets || []).filter((x) => !x.restricted)
                          .map((x) => `${x.ticketId} — ${x.title}`)}
                        allowAdd={false}
                        placeholder={t('ceo.leadTicketPh')}
                        onChange={(v) => {
                          const id = String(v).split(' — ')[0];
                          if (id !== L.ticketId) saveLead(L.leadId, { ticketId: id });
                        }}
                      />
                    </label>
                    <div className="ceo-contactbtns wide">
                      <span className="ceo-contacthint">{t('ceo.leadContactHint')}</span>
                      <button type="button" className="sp-btn" disabled={busy}
                        onClick={() => setLogging({ lead: L.leadId, kind: 'contacted', channel: '', note: '' })}>
                        <MessageSquare size={13} /> {t('ceo.logContacted')}
                      </button>
                      <span className="ceo-lastline">
                        {t('ceo.colLast')}: <b>{fmtDate(L.lastContactAt) || '—'}</b>
                        {L.silentDays !== null && <i> · {t('ceo.noReply', { n: L.silentDays })}</i>}
                        {L.daysAtStage !== null && L.daysAtStage !== undefined && (
                          <i> · {t('ceo.atStageN', { n: L.daysAtStage })}</i>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* OUTSIDE `.ceo-edit`, which is a GRID — inside it the log landed in one
                      narrow column and printed one character per line. The ticket panel has always
                      had its log as a sibling of the fields; this is the same shape. */}
                  <div className="ceo-leadhist">
                    {filesBlock()}

                    {/* A LEAD HAS A HISTORY NOW — sql/20260922o. It could not be asked before
                        "how long has this been sitting at Contacted", and a log added after the
                        fact starts empty, so the six that exist were given their opening row and
                        nothing else invented for them. */}
                    <h4>{t('ceo.logTitle')}</h4>
                    {leadLog === null && <p className="ceo-blkempty">{t('ceo.loading')}</p>}
                    {leadLog !== null && !leadLog.length && (
                      <p className="ceo-blkempty">{t('ceo.noHistory')}</p>
                    )}
                    {!!(leadLog || []).length && (
                      <ul className="ceo-log">
                        {leadLog.map((l) => (
                          <li key={l.id}>
                            <i className={`ceo-ev e-${l.event}`}>{t(`ceo.ev_${l.event}`, l.event)}</i>
                            <span className="ceo-evwhat">
                              {l.field
                                ? `${l.field}: ${l.oldValue || '—'} → ${l.newValue || '—'}`
                                : <>
                                  {!!l.newValue && (l.event === 'contacted' || l.event === 'reply')
                          ? <i className="ceo-chan">{l.newValue}</i>
                          : null}
                                  {l.note || (l.event === 'contacted' || l.event === 'reply' ? '' : l.newValue || '')}
                                </>}
                            </span>
                            <span className="ceo-evwho">{l.by}</span>
                            <span className="ceo-evat">{fmtWhen(l.at)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                </div>
              );
            })()}

            <p className="ceo-note">{t('ceo.leadsNote')}</p>
          </>
        )}

        {/* ── DAILY ──────────────────────────────────────────────────────────────────────────
            The four blocks the morning mail carries. The screen and the mail read the SAME
            endpoint, so a block cannot be right here and wrong in the inbox. */}
        {tab === 'daily' && (
          <>
            <div className="ceo-daily">
              {[['opened', 'dOpened', ''], ['moved', 'dMoved', ''],
                ['overdue', 'dOverdue', 'bad'], ['waiting', 'dWaiting', 'ceo']].map(([k, tk, tone]) => {
                const items = daily?.blocks?.[k] || [];
                return (
                  <section className={`ceo-blk ${tone}`} key={k}>
                    <h3>{t(`ceo.${tk}`)}</h3>
                    <p>{t('ceo.nItems', { n: items.length })}{k === 'overdue' && items.length ? ` · ${t('ceo.reachFirst')}` : ''}</p>
                    {!items.length && <p className="ceo-blkempty">{t('ceo.nothingHere')}</p>}
                    <ul>
                      {items.map((x) => (
                        <li key={x.ticketId}>
                          <button type="button" className="ceo-id"
                            onClick={() => { setTab('tickets'); openOne(x.ticketId); }}>{x.ticketId}</button>
                          <span>{x.title}</span>
                          {!!x.why && <i>{x.why}</i>}
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
            {/* WHERE THE LATE ONES ARE and HOW LONG THIS DESK TAKES — Max, 2026-09-22, asking
                what would help Young manage. Both sit under the four blocks rather than above
                them: the blocks are today's work and these two are the shape behind it, which is
                read second and acted on weekly. */}
            <div className="ceo-daily ceo-daily2">
              <section className="ceo-blk bad">
                <h3>{t('ceo.lateShape')}</h3>
                <p>{t('ceo.lateShapeSub')}</p>
                {!(daily?.overdueShape || []).length
                  ? <p className="ceo-blkempty">{t('ceo.nothingLate')}</p>
                  : (
                    <table className="ceo-mini">
                      <tbody>
                        {daily.overdueShape.map((x) => (
                          <tr key={`${x.desk}-${x.type}`}>
                            <td>{t(`ceo.desk_${x.desk}`)} · {x.type}</td>
                            <td className="n"><b>{x.n}</b></td>
                            <td className="w">{t('ceo.worstN', { n: x.worst })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </section>
              <section className="ceo-blk">
                <h3>{t('ceo.howLong')}</h3>
                <p>{t('ceo.howLongSub')}</p>
                {(() => {
                  const st = daily?.stats;
                  if (!st) return <p className="ceo-blkempty">{t('ceo.nothingHere')}</p>;
                  /* A MEDIAN OF TWO IS NOT A MEASUREMENT. Under three closed tickets the panel
                     says how many it has instead of printing a confident number — the alternative
                     is a figure somebody quotes in a meeting that one ticket could move. */
                  const fig = (x) => (x.n >= 3 && x.median !== null
                    ? <><b>{t('ceo.nDays', { n: x.median })}</b> <em>{t('ceo.medianOf', { n: x.n })}</em></>
                    : <em>{t('ceo.notEnough', { n: x.n })}</em>);
                  return (
                    <table className="ceo-mini">
                      <tbody>
                        <tr><td>{t('ceo.toFirstAnswer')}</td><td className="n">{fig(st.firstResponse)}</td></tr>
                        <tr><td>{t('ceo.toClose')}</td><td className="n">{fig(st.toClose)}</td></tr>
                        <tr className={st.neverAnswered ? 'bad' : ''}>
                          <td>{t('ceo.neverAnswered')}</td>
                          <td className="n"><b>{st.neverAnswered} / {st.openCount}</b></td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </section>
            </div>
            <p className="ceo-note">{t('ceo.dailyNote')}</p>
          </>
        )}

        {/* HOW, AND WHAT CAME BACK — Max, 2026-09-23: the desk should be able to say it went out
            by mail and what the answer was. Before this the log recorded THAT somebody made
            contact on a date and nothing else, which is enough to count from and useless to read.
            BOTH BOXES ARE OPTIONAL and the dialog opens with the note focused, so the fast path is
            type-and-Enter. These two buttons are what every counter on the screen measures from,
            and a required field is how a button stops being pressed — then the dates stop being
            true, which costs more than an empty note. */}
        {logging && (
          <div className="cp-modal-back" onClick={() => setLogging(null)}>
            <div className="cp-modal ceo-modal sm" onClick={(e) => e.stopPropagation()}>
              <h4>{t({ reply: 'ceo.logReply', note: 'ceo.logNote' }[logging.kind] || 'ceo.logContacted')}</h4>
              <p>{t({ reply: 'ceo.logReplySub', note: 'ceo.logNoteSub' }[logging.kind] || 'ceo.logContactedSub')}</p>
              <div className="ceo-formgrid">
                {logging.kind !== 'note' && <label><span>{t('ceo.channel')}</span>
                  {/* Addable, like Type: a desk that works over Zalo should not need a deploy to
                      say so, and the list is whatever the log already holds. */}
                  <ListPicker
                    value={logging.channel}
                    options={channelOptions}
                    placeholder={t('ceo.channelPh')}
                    onChange={(v) => setLogging({ ...logging, channel: v })}
                  />
                </label>}
                {/* FIVE LINES — Max, 2026-09-23. A one-line box says "a few words", and what the
                    other side actually said is usually a short paragraph; a reply summarised into
                    a slot that shows eight words at a time gets summarised until it fits.
                    ENTER NOW MAKES A NEW LINE and Ctrl/Cmd+Enter records: in a multi-line box
                    Enter has to do the thing the box is for, or somebody loses a paragraph
                    halfway through writing it. */}
                <label className="wide"><span>{t('ceo.contactNote')}</span>
                  <textarea
                    autoFocus
                    rows={5}
                    value={logging.note}
                    placeholder={t({ reply: 'ceo.replyNotePh', note: 'ceo.notePh' }[logging.kind] || 'ceo.contactNotePh')}
                    onChange={(e) => setLogging({ ...logging, note: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveContact(); }}
                  />
                </label>
              </div>
              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" onClick={() => setLogging(null)}>{t('ceo.cancel')}</button>
                <button type="button" className="sp-btn sp-btn-primary"
                  disabled={busy || (logging.kind === 'note' && !(logging.note || '').trim())} onClick={saveContact}>
                  {t('ceo.record')}
                </button>
              </div>
            </div>
          </div>
        )}

        {closing && (
          <div className="cp-modal-back" onClick={() => setClosing(null)}>
            <div className="cp-modal ceo-modal sm" onClick={(e) => e.stopPropagation()} data-testid="ceo-close-dialog">
              <h4>{t(closing.mode === 'close' ? 'ceo.closeTitle' : 'ceo.reopenTitle', { id: closing.id })}</h4>
              <p>{t(closing.mode === 'close' ? 'ceo.closeSub' : 'ceo.reopenSub')}</p>
              <div className="ceo-formgrid">
                <label className="wide"><span>{t(closing.mode === 'close' ? 'ceo.closeNote' : 'ceo.reopenNote')}</span>
                  <textarea
                    autoFocus
                    rows={4}
                    value={closing.note}
                    placeholder={t(closing.mode === 'close' ? 'ceo.closeNotePh' : 'ceo.reopenNotePh')}
                    onChange={(e) => setClosing({ ...closing, note: e.target.value, error: '' })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) closeReopen(); }}
                  />
                </label>
              </div>
              {!!closing.error && <p className="ceo-dlgerr" role="alert">{closing.error}</p>}
              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" onClick={() => setClosing(null)}>{t('ceo.cancel')}</button>
                <button type="button" className="sp-btn sp-btn-primary" disabled={busy} onClick={closeReopen}
                  data-testid="ceo-close-confirm">
                  {t(closing.mode === 'close' ? 'ceo.closeTicket' : 'ceo.reopenTicket')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* It NAMES the file and shows what is about to go — size, who put it there, when. A
            confirm that only says "are you sure" makes somebody close it and go back to check,
            which is the reading the dialog was supposed to save them. */}
        {dropFile && (
          <div className="cp-modal-back" onClick={() => setDropFile(null)}>
            <div className="cp-modal ceo-modal sm" onClick={(e) => e.stopPropagation()}>
              <h4>{t('ceo.removeFile')}</h4>
              <p>{t('ceo.removeFileSub')}</p>
              <div className="ceo-dropfile">
                <b>{dropFile.name}</b>
                <span>
                  {fmtBytes(dropFile.bytes)}
                  {dropFile.uploadedBy ? ` · ${dropFile.uploadedBy}` : ''}
                  {dropFile.uploadedAt ? ` · ${fmtDate(dropFile.uploadedAt)}` : ''}
                </span>
              </div>
              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" onClick={() => setDropFile(null)}>
                  {t('ceo.cancel')}
                </button>
                {/* The destructive one is NOT the primary blue: the safe way out is the default,
                    and this button says what it does rather than "OK". */}
                <button type="button" className="sp-btn ceo-danger" disabled={!!upBusy}
                  onClick={() => removeFile(dropFile)}>
                  {t('ceo.removeFile')}
                </button>
              </div>
            </div>
          </div>
        )}

        {addLead && (
          <div className="cp-modal-back" onClick={() => setAddLead(null)}>
            <div className="cp-modal ceo-form" onClick={(e) => e.stopPropagation()}>
              <h4>{t('ceo.newLead')}</h4>
              <p>{t('ceo.newLeadSub')}</p>
              <div className="ceo-formgrid">
                <label className="wide"><span>{t('ceo.colCounterparty')} <b>*</b></span>
                  <input value={addLead.counterparty} autoFocus
                    placeholder={t('ceo.counterpartyPh')}
                    onChange={(e) => setAddLead({ ...addLead, counterparty: e.target.value })} />
                </label>
                {/* The countries already in the pipeline, and a new one addable — new markets do
                    appear, which is what a lead pipeline is for. */}
                <label><span>{t('ceo.colCountry')}</span>
                  <ListPicker
                    value={addLead.country || ''}
                    options={[...new Set((leads?.leads || []).map((r) => r.country).filter(Boolean))].sort()}
                    placeholder={t('ceo.countryPh')}
                    onChange={(v) => setAddLead({ ...addLead, country: v })}
                  />
                </label>
                {/* Sector is the same kind of field as Type and gets the same picker, so the two
                    do not teach two ways of answering one shape of question. */}
                <label><span>{t('ceo.colSector')}</span>
                  <ListPicker
                    value={addLead.sector || ''}
                    options={[...new Set((leads?.leads || []).map((r) => r.sector).filter(Boolean))].sort()}
                    placeholder={t('ceo.sectorPh')}
                    onChange={(v) => setAddLead({ ...addLead, sector: v })}
                  />
                </label>
                <label><span>{t('ceo.colStage')}</span>
                  <select value={addLead.stage}
                    onChange={(e) => setAddLead({ ...addLead, stage: e.target.value })}>
                    {(leads?.stages || []).map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </label>
                <label><span>{t('ceo.colNext')}</span>
                  <input type="date" value={addLead.nextContact || ''}
                    onChange={(e) => setAddLead({ ...addLead, nextContact: e.target.value })} />
                </label>
                {/* CRITERIA — Max, 2026-09-23: "thiếu Criteria". The server already took it; only
                    the form never asked. It is the terms the CEO set for this lead, which is the
                    reason the lead is being opened at all, so leaving it off meant the one thing
                    somebody knows at that moment had to be typed on a second visit.
                    It spans the row, like the counterparty and the ticket: a sentence in a
                    half-width box is a sentence read four words at a time. */}
                <label className="wide"><span>{t('ceo.colCriteria')}</span>
                  <input
                    value={addLead.criteria || ''}
                    placeholder={t('ceo.criteriaPh')}
                    onChange={(e) => setAddLead({ ...addLead, criteria: e.target.value })}
                  />
                </label>
                {/* A CLOSED LIST — `allowAdd={false}`. A ticket id either exists or it does not,
                    and typing one by hand is exactly how a lead comes to point at a ticket nobody
                    raised. The options are the real tickets, shown with their titles so somebody
                    picks the thing rather than remembering a number; the server checks it too,
                    because a rule that lives in the form is not a rule. */}
                <label className="wide"><span>{t('ceo.colTicket')}</span>
                  <ListPicker
                    value={addLead.ticketId || ''}
                    options={(data?.tickets || []).filter((r) => !r.restricted)
                      .map((r) => `${r.ticketId} — ${r.title}`)}
                    allowAdd={false}
                    placeholder={t('ceo.leadTicketPh')}
                    onChange={(v) => setAddLead({ ...addLead, ticketId: String(v).split(' — ')[0] })}
                  />
                </label>
              </div>
              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" onClick={() => setAddLead(null)}>{t('ceo.cancel')}</button>
                <button type="button" className="sp-btn sp-btn-primary"
                  disabled={busy || !addLead.counterparty.trim()} onClick={createLead}>
                  {t('ceo.create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* THE CC LIST, BUILT AND EMPTY — Max, 2026-09-22: "làm sẵn nhưng để trống". Entity and
            internal contact are what a ticket's outgoing mail copies (Young, rule 3), so each of
            the eight needs a real address; Young was asked for them on 2026-09-15 and has not sent
            them. The boxes are here so that when he does it is one screen, and empty until then
            because a guessed address mails a stranger. */}
        {canEditEntities && tab === 'tickets' && (
          <details className="ceo-entities">
            <summary>
              {t('ceo.entityTitle')}
              <i>{t('ceo.entityFilled', {
                n: (data?.entities || []).filter((e) => e.cc).length,
                total: (data?.entities || []).length,
              })}</i>
            </summary>
            <p>{t('ceo.entitySub')}</p>
            <div className="ceo-entgrid">
              {(data?.entities || []).map((e) => (
                <label key={e.code}>
                  <span>{e.code}<i>{e.name}</i></span>
                  <input
                    type="email"
                    placeholder={t('ceo.entityPh')}
                    defaultValue={e.cc}
                    onBlur={(ev) => {
                      if (ev.target.value === e.cc) return;
                      const box = ev.target;
                      saveEntity(e.code, box.value, () => { box.value = e.cc || ''; });
                    }}
                  />
                </label>
              ))}
            </div>
          </details>
        )}

        {adding && (
          <div className="cp-modal-back" onClick={() => setAdding(null)}>
            {/* ITS OWN FIELD STYLES, not the calculator's. This form borrowed `.cp-field` from
                PricingCalculator.css, which is written for that screen's grid — dropped into a
                modal it collapsed the gap between every label and its box. Max, 2026-09-22, having
                said the forms were poor more than once: the fault was that the checks screenshotted
                the SCREEN and never the dialog on top of it. */}
            <div className="cp-modal ceo-form" onClick={(e) => e.stopPropagation()}>
              <h4>{t('ceo.newTicket')}</h4>
              <p>{t('ceo.newSub')}</p>

              <div className="ceo-formgrid">
                {desks.length > 1 && (
                  <label><span>{t('ceo.desk')}</span>
                    <select value={adding.desk} onChange={(e) => setAdding({ ...adding, desk: e.target.value })}>
                      {desks.map((d) => <option key={d} value={d}>{t(`ceo.desk_${d}`)}</option>)}
                    </select>
                  </label>
                )}
                {/* "Title / from" is the TABLE's column name, where the two are printed together.
                    In a form they are two questions and each gets its own. */}
                <label className="wide">
                  <span>{t('ceo.fTitle')} <b>*</b></span>
                  <input
                    value={adding.title}
                    autoFocus
                    placeholder={t('ceo.fTitlePh')}
                    onChange={(e) => setAdding({ ...adding, title: e.target.value })}
                  />
                </label>
                <label className="wide">
                  <span>{t('ceo.fromWho')}</span>
                  <input
                    value={adding.sender || ''}
                    placeholder={t('ceo.fFromPh')}
                    onChange={(e) => setAdding({ ...adding, sender: e.target.value })}
                  />
                </label>
                {/* THE FORM ASKS WHAT THE PANEL SHOWS — Max, 2026-09-23: "cái modal new ticket
                    nó thiếu vài trường so với open á". Entity and internal contact were left off
                    deliberately, on Young's rule 3 that most items start with no home — but the
                    rule is that they MAY be empty, not that they may not be answered. Somebody
                    raising a ticket who already knows the company had to save it, reopen it and
                    fill the same field in twice. Status and Owner had no reason at all.
                    SAME ORDER AS THE PANEL, and the same controls, so the form and the thing it
                    creates are one screen read twice rather than two screens to learn. */}
                <label><span>{t('ceo.colType')}</span>
                  <ListPicker
                    value={adding.type || ''}
                    options={typeOptions}
                    placeholder={t('ceo.fTypePh')}
                    onChange={(v) => setAdding({ ...adding, type: v })}
                  />
                </label>
                <label><span>{t('ceo.colStatus')}</span>
                  <select value={adding.status || 'Open'}
                    onChange={(e) => setAdding({ ...adding, status: e.target.value })}>
                    {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </label>
                <label><span>{t('ceo.colEntity')}</span>
                  <select value={adding.entity || ''}
                    onChange={(e) => setAdding({ ...adding, entity: e.target.value })}>
                    <option value="">{t('ceo.notSet')}</option>
                    {(data?.entities || []).map((x) => <option key={x.code} value={x.code}>{x.code}</option>)}
                  </select>
                </label>
                <label><span>{t('ceo.colContact')}</span>
                  <input
                    value={adding.internalContact || ''}
                    placeholder={t('ceo.fLaterPh')}
                    onChange={(e) => setAdding({ ...adding, internalContact: e.target.value })}
                  />
                </label>
                <label><span>{t('ceo.colOwner')}</span>
                  <input
                    value={adding.owner || ''}
                    placeholder={t('ceo.fLaterPh')}
                    onChange={(e) => setAdding({ ...adding, owner: e.target.value })}
                  />
                </label>
                <label><span>{t('ceo.colDue')}</span>
                  <input type="date" value={adding.dueDate || ''}
                    onChange={(e) => setAdding({ ...adding, dueDate: e.target.value })} />
                </label>
              </div>

              {/* The note stays, but says what it now means: these MAY be left empty, which is
                  Young's rule 3. It used to say they could not be answered here at all. */}
              <p className="ceo-formnote">{t('ceo.newLater')}</p>

              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" onClick={() => setAdding(null)}>{t('ceo.cancel')}</button>
                <button
                  type="button"
                  className="sp-btn sp-btn-primary"
                  disabled={busy || !adding.title.trim()}
                  onClick={create}
                >
                  {t('ceo.create')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
